import {
  AREA_OF_CONCERN,
  CARIES,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'PERIAPICAL_RADIOLUCENCY' is defined but never used.
  PERIAPICAL_RADIOLUCENCY,
  RADIOGRAPHIC_BONELOSS,
  SUPPORTED_MODEL_TYPES,
} from '../../constants';
import { makeAPIRequest } from '../../utils/api';
import { findRBLAnnotationIndex } from '../../utils/rbl';

import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'getAugmentedPathologiesForImage' is defined but never used.
  getAugmentedPathologiesForImage,
  getPatientPath,
} from './annotation-utils/annotation-utils';
import AnnotatorConstants from './annotator.constants';
import { createUserAnnotation } from './helpers';
import type { CornerstoneFriendlyAnnotation } from './types';
export function resetImagesState() {
  return {
    type: AnnotatorConstants.RESET_IMAGES,
  };
}

export function loginAsDemoUser() {
  return {
    type: AnnotatorConstants.HANDLE_LOGIN_AS_DEMO_USER,
  };
}

export const savePatientData =
  (patientId: any, age: any, gender: any) => async (dispatch: any) => {
    try {
      const data = {
        patientId,
        age,
        gender,
      };

      dispatch({
        type: AnnotatorConstants.SAVE_PATIENT_DATA_SUCCESS,
        data,
      });
    } catch (e) {
      dispatch({
        type: AnnotatorConstants.SAVE_PATIENT_DATA_FAILURE,
      });
    }
  };

export const viewPatient = (patientId: any) => ({
  type: AnnotatorConstants.VIEW_PATIENT,

  data: {
    patientId,
  },
});

export const fetchPatientAnalyses = (user: any, patient: any) => {
  const patientPath = getPatientPath(patient);

  const data = {
    sessionToken: user.sessionToken,
    userId: user.objectId,
    patientPath,
    patientId: patient.patientId,
    // TODO: Remove the following property once added on backend
    practiceIdentifier: patient.patientId,
  };

  return async (dispatch: any) => {
    try {
      const response = await makeAPIRequest(
        '/functions/getPatientAnalyses',
        { data },
        'POST',
      );

      return dispatch({
        type: AnnotatorConstants.FETCH_PATIENT_ANALYSES_SUCCESS,
        data: {
          patientId: patient.patientId,
        },
        response,
      });
    } catch (e) {
      return dispatch({
        type: AnnotatorConstants.FETCH_PATIENT_ANALYSES_FAILURE,
        error: e,
      });
    }
  };
};

export const saveUserModificationsAction = (
  // @ts-expect-error TS(7006): Parameter 'patientId' implicitly has an 'any' type... Remove this comment to see the full error message
  patientId,
  // @ts-expect-error TS(7006): Parameter 'idOfModifiedImage' implicitly has an 'a... Remove this comment to see the full error message
  idOfModifiedImage,
  // @ts-expect-error TS(7006): Parameter 'modificationsFromImageToolbar' implicit... Remove this comment to see the full error message
  modificationsFromImageToolbar,
) => ({
  type: AnnotatorConstants.SAVE_USER_MODIFICATIONS_SUCCESS,

  response: {
    data: {
      patientId,
      imageId: idOfModifiedImage,
      modificationsFromImageToolbar,
    },
  },
});

/**
 * Updates a finding object whenever the validation or its reason changes, adding to the action history
 *
 * @param finding
 * @param newValidation
 * @param newValidationReason
 * @param userId
 */
export const computeUpdatedFinding = (
  finding: any,
  newValidation: any,
  newValidationReason: any,
  newValidationReasons: any,
) => {
  const findingCopy = { ...finding };

  // When changing validations, the reason for agreement or disagreement no longer applies
  findingCopy.validationReason = newValidation ? '' : newValidationReason;
  findingCopy.validationReasons = newValidation ? [] : newValidationReasons;

  // Multi reason array
  if (newValidationReasons) {
    findingCopy.validationReasons = newValidationReasons;
  }

  // Update the user finding object, saving the validation and also cleaning up the "hover" attribute only used locally
  if (newValidation) {
    findingCopy.validation = newValidation;
    findingCopy.userValidation = newValidation;
  }

  return findingCopy;
};

const isMatchingSystemFinding = (f: any, targetFinding: any) =>
  f.id && f.id === targetFinding.id;
const isMatchingUserFinding = (f: any, targetFinding: any) =>
  f.annotationId && f.annotationId === targetFinding.annotationId;

/**
 * Takes in an image, which contains a payload with user and system findings. Returns the finding's index in that object
 *
 * @param image
 * @param annotationGroupName
 * @param targetFinding
 */
export const computeFindingIndex = (
  image: any,
  annotationGroupName: any,
  targetFinding: any,
): number => {
  let findingList = [];
  if (annotationGroupName === 'userAnnotations') {
    findingList = image.userAnnotations;
  } else {
    for (const [modelKey, modelObject] of Object.entries(image?.models ?? {})) {
      if (modelKey === annotationGroupName) {
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        findingList = modelObject.findings;
        break;
      }
    }
  }
  return findingList.findIndex(
    (f: any) =>
      isMatchingUserFinding(f, targetFinding) ||
      isMatchingSystemFinding(f, targetFinding),
  );
};

export const determineAnnotationGroupKey = (finding: any) => {
  const { type, annotationId } = finding;
  if (type === AREA_OF_CONCERN) {
    return CARIES;
  }
  if (annotationId) {
    return 'userAnnotations';
  }

  return type;
};

/**
 * When hovering a finding, or updating its validation or reason, propagate the change to the application state
 *
 * @param {*} selectedPatientId
 * @param {*} image
 * @param {*} user
 * @param {*} imageAnalyses
 * @param {*} finding
 * @param {*} newValidationValue
 * @param {*} newValidationReason
 */
export function updateFindingAction(
  selectedPatientId: any,
  image: any,
  finding: any,
  newValidationValue: any,
) {
  // Determine whether we are editing a system or user finding
  const annotationGroupName = determineAnnotationGroupKey(finding);
  const index = computeFindingIndex(image, annotationGroupName, finding);
  // @ts-expect-error TS(2554): Expected 4 arguments, but got 2.
  const updatedFinding = computeUpdatedFinding(finding, newValidationValue);
  let updatedImage = {};
  let rblIndexTuple = [];
  if (newValidationValue) {
    if (annotationGroupName === RADIOGRAPHIC_BONELOSS) {
      // We can only validate RBL findings that form a line and are severe or moderate.
      // This eludes that the finding will contain abd and cej points. We look at point[0] to help us determine which RBL finding was validated.
      const rblPointId = finding?.points[0]?.id;
      rblIndexTuple = findRBLAnnotationIndex(
        rblPointId,
        image.models[annotationGroupName].findings,
      );
    }
    updatedImage = updateFindingInImage(
      image,
      annotationGroupName,
      index,
      updatedFinding,
      rblIndexTuple,
    );
  } else {
    // This function is also called when hovering the finding
    // @ts-expect-error TS(2554): Expected 5 arguments, but got 4.
    updatedImage = updateFindingInImage(
      image,
      annotationGroupName,
      index,
      finding,
    );
  }

  const isSystemFinding = SUPPORTED_MODEL_TYPES.includes(annotationGroupName);
  return async (dispatch: any) => {
    if (isSystemFinding) {
      return dispatch({
        type: AnnotatorConstants.UPDATE_AI_RESULT_SUCCESS,
        response: {
          data: { updatedImage, selectedPatientId },
        },
      });
    }

    return dispatch({
      type: AnnotatorConstants.UPDATE_USER_FINDING_SUCCESS,
      response: {
        data: {
          selectedPatientId,
          updatedImage,
        },
      },
    });
  };
}

const updateImageAnalysis =
  (selectedPatientId: any, image: any, successEvent: any, failureEvent: any) =>
  async (dispatch: any) => {
    try {
      return dispatch({
        type: successEvent,
        data: {
          selectedPatientId,
          image,
        },
      });
    } catch (e) {
      return dispatch({
        type: failureEvent,
        data: {
          selectedPatientId,
          image,
        },
      });
    }
  };

export const deleteUserAnnotationAction = (
  selectedPatientId: any,
  annotation: any,
  images: any,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'imageAnalyses' is defined but never used.
  imageAnalyses: any,
) => {
  const { imageId } = annotation;
  const currentImage = { ...images[imageId] };
  const { userAnnotations } = currentImage;
  const newAnnotations = userAnnotations.filter(
    (a: any) => a.annotationId !== annotation.annotationId,
  );

  currentImage.userAnnotations = newAnnotations;

  // TODO: this code can be reused once we reintegrate saveAiAnalysis
  // for (let i = 0; i < userAnnotations.length; i++) {
  //   if (userAnnotations[i].objectId === annotation.objectId) {
  //     userAnnotations[i].deleted = true;
  //     break;
  //   }
  // }

  return updateImageAnalysis(
    selectedPatientId,
    currentImage,
    AnnotatorConstants.DELETE_USER_ANNOTATION_SUCCESS,
    AnnotatorConstants.DELETE_USER_ANNOTATION_FAILURE,
  );
};

export function addAnnotationAction(
  selectedPatientId: any,
  object: any,
  newAnnotation: CornerstoneFriendlyAnnotation,
  annotationId: any,
) {
  const updatedAnnotation = createUserAnnotation(
    {
      id: annotationId,
      ...newAnnotation,
    },
    object,
    newAnnotation.type!,
  );

  const image = { ...object };
  const existingUserAnnotations = image.userAnnotations ?? [];
  existingUserAnnotations.push(updatedAnnotation);
  image.userAnnotations = existingUserAnnotations;

  // @ts-expect-error TS(2554): Expected 4 arguments, but got 3.
  return updateImageAnalysis(
    selectedPatientId,
    image,
    AnnotatorConstants.ADD_USER_ANNOTATION_SUCCESS,
  );
}

/**
 * When a tooth number is edited, propagate the change to the application state
 *
 * @param {*} selectedPatientId
 * @param {*} image
 * @param {*} user
 * @param {*} imageAnalyses
 * @param {*} finding
 * @param {*} newToothNumber
 * @param {*} oldToothNumber
 * @param {*} editedViaFindingViewer
 */
export function updateToothNumberAction(
  selectedPatientId: any,
  image: any,
  user: any,
  imageAnalyses: any,
  finding: any,
  newToothNumber: any,
  oldToothNumber: any,
  editedViaFindingViewer = false,
) {
  // only update one finding on findingViewer
  const annotationGroupName = finding.certainty
    ? 'pathologies'
    : 'userAnnotations';
  const updatedImage = image;

  if (editedViaFindingViewer) {
    const index = computeFindingIndex(
      updatedImage,
      annotationGroupName,
      finding,
    );
    updatedImage.findings[annotationGroupName][index].toothNumber =
      newToothNumber;
  } else {
    // update all findings with the corresponding tooth number
    for (const findingIndex in updatedImage.findings.pathologies) {
      if (
        updatedImage.findings.caries[findingIndex].toothNumber ===
        oldToothNumber
      ) {
        updatedImage.findings.caries[findingIndex].toothNumber = newToothNumber;
      }
    }
    for (const findingIndex in updatedImage.findings.userAnnotations) {
      if (
        updatedImage.findings.userAnnotations[findingIndex].toothNumber ===
        oldToothNumber
      ) {
        updatedImage.findings.userAnnotations[findingIndex].toothNumber =
          newToothNumber;
      }
    }
  }

  return async (dispatch: any) =>
    dispatch({
      type: AnnotatorConstants.UPDATE_TOOTH_NUMBER,
      response: {
        data: { updatedImage, selectedPatientId },
      },
    });
}

const updateFindingInImage = (
  image: any,
  annotationGroupName: any,
  index: any,
  updatedFinding: any,
  rblIndexTuple: any,
) => {
  const newImage = { ...image };
  if (annotationGroupName === RADIOGRAPHIC_BONELOSS && rblIndexTuple) {
    newImage.models.radiographic_boneloss.findings[rblIndexTuple[0]].sides[
      rblIndexTuple[1]
    ] = updatedFinding;
  } else if (annotationGroupName === 'userAnnotations') {
    newImage.userAnnotations[index] = updatedFinding;
  } else {
    newImage.models[annotationGroupName].findings[index] = updatedFinding;
  }
  return newImage;
};

export const swapImageIndexes =
  (selectedPatientId: any, image1: any, image2: any) => async (dispatch: any) =>
    dispatch({
      type: AnnotatorConstants.SWAP_IMAGE_INDICES,
      response: {
        data: {
          selectedPatientId,
          image1,
          image2,
        },
      },
    });

export const updateUserName = (id: any, name: any) => (dispatch: any) =>
  dispatch({ type: AnnotatorConstants.UPDATE_USER_NAME, data: { id, name } });

export const clearPatientImages = (id: string) => (dispatch: any) =>
  dispatch({ type: AnnotatorConstants.CLEAR_PATIENT_IMAGES, data: { id } });
